import { NcErrorType } from '../../lib/globals';
import { ncIsNumber } from '../../lib/is';
export const presetErrorCodexMap = {
    [NcErrorType.ERR_UNKNOWN]: {
        message: 'Something went wrong',
        code: 500,
    },
    [NcErrorType.ERR_INTERNAL_SERVER]: {
        message: (message) => message || `Internal server error`,
        code: 500,
    },
    [NcErrorType.ERR_DATABASE_OP_FAILED]: {
        message: (message) => message || `There was an error while running the query`,
        code: 500,
    },
    [NcErrorType.ERR_AUTHENTICATION_REQUIRED]: {
        message: (message) => message
            ? `Authentication required - ${message}`
            : 'Authentication required to access this resource',
        code: 401,
    },
    [NcErrorType.ERR_FORBIDDEN]: {
        message: (message) => message ? `Forbidden - ${message}` : 'Forbidden to access this resource',
        code: 403,
    },
    [NcErrorType.ERR_INSUFFICIENT_PRIVILEGE]: {
        message: (message) => message || 'Insufficient privilege to perform this action',
        code: 403,
    },
    [NcErrorType.ERR_API_TOKEN_NOT_ALLOWED]: {
        message: 'This request is not allowed with API token',
        code: 401,
    },
    [NcErrorType.ERR_WORKSPACE_NOT_FOUND]: {
        message: (id) => `Workspace '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_BASE_NOT_FOUND]: {
        message: (id) => `Base '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_VIEW_COLUMN_NOT_FOUND]: {
        message: (id) => `View column '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_SOURCE_NOT_FOUND]: {
        message: (id) => `Source '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_INTEGRATION_NOT_FOUND]: {
        message: (id) => `Connection '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_INTEGRATION_LINKED_WITH_BASES]: {
        message: (bases) => `Connection linked with following bases '${bases}'`,
        code: 404,
    },
    [NcErrorType.ERR_TABLE_NOT_FOUND]: {
        message: (id) => `Table '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_VIEW_NOT_FOUND]: {
        message: (id) => `View '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_FIELD_NOT_FOUND]: {
        message: (id) => `Field ${id} not found`,
        code: 404,
    },
    [NcErrorType.ERR_HOOK_NOT_FOUND]: {
        message: (id) => `Hook '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_RECORD_NOT_FOUND]: {
        message: (...ids) => {
            const isMultiple = Array.isArray(ids) && ids.length > 1;
            return `Record${isMultiple ? 's' : ''} '${ids.join(', ')}' not found`;
        },
        code: 404,
    },
    [NcErrorType.ERR_TABLE_TRASH_NOT_SUPPORTED]: {
        message: (tableTitle) => `Record trash is not supported for table '${tableTitle || 'unknown'}'`,
        code: 422,
    },
    [NcErrorType.ERR_RECORD_RESTORE_CONFLICT]: {
        message: (details) => `Cannot restore record — link conflict: ${details || 'unknown conflict'}`,
        code: 409,
    },
    [NcErrorType.ERR_RECORD_NOT_TRASHED]: {
        message: () => `Cannot permanently delete active records that are not in trash`,
        code: 422,
    },
    [NcErrorType.ERR_TRASH_BATCH_LIMIT_EXCEEDED]: {
        message: (limit) => `Cannot process more than ${limit} records at a time`,
        code: 422,
    },
    [NcErrorType.ERR_GENERIC_NOT_FOUND]: {
        message: (resource, id) => `${resource} '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_WIDGET_NOT_FOUND]: {
        message: (id) => `Widget '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_VIEW_SECTION_NOT_FOUND]: {
        message: (id) => `View section '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_TRASH_NOT_FOUND]: {
        message: (id) => `Trash entry '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_PARENT_IN_TRASH]: {
        message: (parentType) => `Cannot restore — parent ${parentType} is in trash. Restore it first.`,
        code: 400,
    },
    [NcErrorType.ERR_DASHBOARD_NOT_FOUND]: {
        message: (id) => `Dashboard '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_CHAT_SESSION_NOT_FOUND]: {
        message: (id) => `Chat session '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_CHAT_MESSAGE_NOT_FOUND]: {
        message: (id) => `Chat message '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_WORKFLOW_NOT_FOUND]: {
        message: (id) => `Workflow '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_SCRIPT_NOT_FOUND]: {
        message: (id) => `Script '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_RLS_POLICY_NOT_FOUND]: {
        message: (id) => `RLS Policy '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_REQUIRED_FIELD_MISSING]: {
        message: (field) => `Field '${field}' is required`,
        code: 422,
    },
    [NcErrorType.ERR_DUPLICATE_RECORD]: {
        message: (...ids) => {
            const isMultiple = Array.isArray(ids) && ids.length > 1;
            return `Record${isMultiple ? 's' : ''} '${ids.join(', ')}' already exists`;
        },
        code: 422,
    },
    [NcErrorType.ERR_USER_NOT_FOUND]: {
        message: (idOrEmail) => {
            const isEmail = idOrEmail.includes('@');
            return `User ${isEmail ? 'with email' : 'with id'} '${idOrEmail}' not found`;
        },
        code: 404,
    },
    [NcErrorType.ERR_TEAM_NOT_FOUND]: {
        message: (id) => `Team '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_INVALID_OFFSET_VALUE]: {
        message: (offset) => ncIsNumber(Number(offset)) && Number(offset) > 0
            ? `Offset value '${offset}' is invalid`
            : `Offset must be a non-negative integer`,
        code: 422,
    },
    [NcErrorType.ERR_INVALID_PAGE_VALUE]: {
        message: (page) => `Page value '${page}' is invalid`,
        code: 422,
    },
    [NcErrorType.ERR_INVALID_PK_VALUE]: {
        message: (value, pkColumn) => `Primary key value '${value}' is invalid for column '${pkColumn}'`,
        code: 422,
    },
    [NcErrorType.ERR_INVALID_LIMIT_VALUE]: {
        message: (limitMin, limitMax) => `Limit value should be between ${limitMin} and ${limitMax}`,
        code: 422,
    },
    [NcErrorType.ERR_INVALID_FILTER]: {
        message: (filter) => `Filter '${filter}' is invalid`,
        code: 422,
    },
    [NcErrorType.ERR_UNSUPPORTED_FILTER_OPERATION]: {
        message: (operation) => `Operation '${operation}' is not supported`,
        code: 400,
    },
    [NcErrorType.ERR_INVALID_SHARED_VIEW_PASSWORD]: {
        message: 'Invalid shared view password',
        code: 403,
    },
    [NcErrorType.ERR_SHARED_DASHBOARD_PASSWORD_INVALID]: {
        message: 'Invalid shared dashboard password',
        code: 403,
    },
    [NcErrorType.ERR_INVALID_ATTACHMENT_JSON]: {
        message: (payload) => `Invalid JSON for attachment field: ${payload}`,
        code: 400,
    },
    [NcErrorType.ERR_NOT_IMPLEMENTED]: {
        message: (feature) => `${feature} is not implemented`,
        code: 501,
    },
    [NcErrorType.ERR_INVALID_JSON]: {
        message: 'Invalid JSON in request body',
        code: 400,
    },
    [NcErrorType.ERR_COLUMN_ASSOCIATED_WITH_LINK]: {
        message: 'Column is associated with a link, please remove the link first',
        code: 400,
    },
    [NcErrorType.ERR_TABLE_ASSOCIATED_WITH_LINK]: {
        message: 'Table is associated with a link, please remove the link first',
        code: 400,
    },
    [NcErrorType.ERR_FORMULA]: {
        message: (message) => {
            return message;
        },
        code: 400,
    },
    [NcErrorType.ERR_CIRCULAR_REF_IN_FORMULA]: {
        message: (message) => {
            return message;
        },
        code: 400,
    },
    [NcErrorType.ERR_PERMISSION_DENIED]: {
        message: 'Permission denied',
        code: 403,
    },
    [NcErrorType.ERR_INVALID_ATTACHMENT_UPLOAD_SCOPE]: {
        message: 'Invalid attachment upload scope',
        code: 400,
    },
    [NcErrorType.ERR_REORDER_FAILED]: {
        message: 'Reorder failed',
        code: 400,
    },
    [NcErrorType.ERR_CANNOT_CALCULATE_INTERMEDIATE_ORDER]: {
        message: 'Cannot calculate intermediate order',
        code: 400,
    },
    [NcErrorType.ERR_PLAN_LIMIT_EXCEEDED]: {
        message: (message) => message || 'Plan limit exceeded',
        code: 403,
    },
    [NcErrorType.ERR_SSO_LOGIN_REQUIRED]: {
        message: (_workspaceId) => 'SSO login required for workspace',
        code: 403,
    },
    [NcErrorType.ERR_SSO_GENERATED_TOKEN_REQUIRED]: {
        message: (_workspaceId) => 'This workspace requires SSO-authenticated tokens. Please generate a new token after signing in with SSO',
        code: 403,
    },
    [NcErrorType.ERR_MFA_SETUP_REQUIRED]: {
        message: (_workspaceId) => 'Two-factor authentication setup required for this workspace',
        code: 403,
    },
    [NcErrorType.ERR_MAX_PAYLOAD_LIMIT_EXCEEDED]: {
        message: (limit) => `Maximum ${limit} entities are allowed per request`,
        code: 422,
    },
    [NcErrorType.ERR_MAX_WORKSPACE_LIMIT_REACHED]: {
        message: () => `The maximum workspace limit has been reached. Please contact your administrator to request access to a workspace.`,
        code: 403,
    },
    [NcErrorType.ERR_INVALID_VALUE_FOR_FIELD]: {
        message: (message) => message,
        code: 422,
    },
    [NcErrorType.ERR_INVALID_REQUEST_BODY]: {
        message: (message) => message,
        code: 400,
    },
    [NcErrorType.ERR_BASE_COLLABORATION]: {
        message: (message) => message || 'Something went wrong',
        code: 422,
    },
    [NcErrorType.ERR_ORG_USER]: {
        message: (message) => message || 'Something went wrong',
        code: 422,
    },
    [NcErrorType.ERR_TABLE_OP_FAILED]: {
        message: (message) => message || 'Something went wrong',
        code: 422,
    },
    [NcErrorType.ERR_COLUMN_OP_FAILED]: {
        message: (message) => message || 'Something went wrong',
        code: 422,
    },
    [NcErrorType.ERR_SYNC_TABLE_OPERATION_PROHIBITED]: {
        message: (message) => message ||
            `Prohibited data insert / update / delete operation on synced table`,
        code: 422,
    },
    [NcErrorType.ERR_FEATURE_NOT_SUPPORTED]: {
        message: (message) => message || `Upgrade to a higher plan to use this feature.`,
        code: 403,
    },
    [NcErrorType.ERR_DUPLICATE_IN_ALIAS]: {
        message: (message) => message,
        code: 422,
    },
    [NcErrorType.ERR_OUT_OF_SYNC]: {
        message: (message) => message || `Please refresh the page and try again.`,
        code: 409,
    },
    [NcErrorType.ERR_FILTER_VERIFICATION_FAILED]: {
        message: (message) => `Filter verification failed: ${message}`,
        code: 422,
    },
    [NcErrorType.ERR_WEBHOOK_ERROR]: {
        message: (message) => message,
        code: 400,
    },
    [NcErrorType.ERR_DATA_SOURCES_NOT_FOUND]: {
        message: (message) => message,
        code: 400,
    },
    [NcErrorType.ERR_TEST_PLUGIN_FAILED]: {
        message: (message) => message,
        code: 400,
    },
    [NcErrorType.ERR_UNSUPPORTED_RELATION]: {
        message: (message) => message,
        code: 400,
    },
    [NcErrorType.ERR_IN_EXTERNAL_DATA_SOURCE]: {
        message: (message) => message ||
            'Error running query on external source. Confirm if source is accessible.',
        code: 400,
    },
    [NcErrorType.ERR_EXTERNAL_DATA_SOURCE_TIMEOUT]: {
        message: (message) => message ||
            'External source taking long to respond. Reconsider sorts/filters for this view and confirm if source is accessible.',
        code: 408,
    },
    [NcErrorType.ERR_RELATION_FIELD_NOT_FOUND]: {
        message: (id) => `Relation Field '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_EXTENSION_NOT_FOUND]: {
        message: (id) => `Extension '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_STORAGE_FILE_CREATE]: {
        message: (details) => `Failed to create file: ${details}`,
        code: 500,
    },
    [NcErrorType.ERR_STORAGE_FILE_READ]: {
        message: (details) => `Failed to read file: ${details}`,
        code: 500,
    },
    [NcErrorType.ERR_STORAGE_FILE_DELETE]: {
        message: (details) => `Failed to delete file: ${details}`,
        code: 500,
    },
    [NcErrorType.ERR_STORAGE_FILE_STREAM]: {
        message: (details) => `Failed to stream file: ${details}`,
        code: 500,
    },
    [NcErrorType.ERR_SUBSCRIPTION_ALREADY_EXISTS]: {
        message: (id) => `Subscription already exists for ${id}`,
        code: 409,
    },
    [NcErrorType.ERR_SUBSCRIPTION_NOT_FOUND]: {
        message: (id) => `Subscription not found for ${id}`,
        code: 404,
    },
    [NcErrorType.ERR_PLAN_NOT_AVAILABLE]: {
        message: () => 'This plan is not available',
        code: 400,
    },
    [NcErrorType.ERR_SEAT_COUNT_MISMATCH]: {
        message: (msg) => msg,
        code: 400,
    },
    [NcErrorType.ERR_INVALID_PAYMENT_PAYLOAD]: {
        message: (msg) => msg,
        code: 400,
    },
    [NcErrorType.ERR_STRIPE_CUSTOMER_NOT_FOUND]: {
        message: (id) => `Stripe customer '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_STRIPE_SUBSCRIPTION_NOT_FOUND]: {
        message: (id) => `Stripe subscription '${id}' not found`,
        code: 404,
    },
    [NcErrorType.ERR_SUBSCRIPTION_OWNERSHIP_MISMATCH]: {
        message: (msg) => msg,
        code: 403,
    },
    [NcErrorType.ERR_INTERNAL_CUSTOMER_NOT_SUPPORTED]: {
        message: (msg) => msg,
        code: 501,
    },
    [NcErrorType.ERR_SUBSCRIPTION_CREATE_FAILED]: {
        message: (msg) => msg,
        code: 500,
    },
    [NcErrorType.ERR_STRIPE_WEBHOOK_VERIFICATION_FAILED]: {
        message: (msg) => msg,
        code: 400,
    },
    [NcErrorType.ERR_PLAN_ALREADY_EXISTS]: {
        message: (id) => `Plan already exists with id ${id}`,
        code: 409,
    },
    [NcErrorType.ERR_WORKFLOW_WAITING_EXECUTIONS]: {
        message: (count) => `This workflow has ${count} pending execution(s) waiting to resume`,
        code: 400,
    },
    [NcErrorType.ERR_WORKFLOW_NODE_NOT_FOUND]: {
        message: (message) => message,
        code: 400,
    },
    [NcErrorType.ERR_METHOD_NOT_ALLOWED]: {
        message: (method) => `Method ${method} not allowed`,
        code: 405,
    },
    [NcErrorType.ERR_LICENSE_REQUIRED]: {
        message: (feature) => feature
            ? `The "${feature}" feature requires an Enterprise license.`
            : 'This feature requires an Enterprise license.',
        code: 402,
    },
    [NcErrorType.ERR_LICENSE_SUSPENDED]: {
        message: 'Your license has been suspended. Please contact support.',
        code: 403,
    },
    [NcErrorType.ERR_SYSTEM_FIELD_NON_MODIFIABLE]: {
        message: 'System field cannot be modified',
        code: 422,
    },
    [NcErrorType.ERR_SYSTEM_MISCONFIGURED]: {
        message: (message) => message || 'System is not configured correctly',
        code: 500,
    },
    [NcErrorType.ERR_TOO_MANY_REQUESTS]: {
        message: (message) => message || 'Too many requests. Please try again later.',
        code: 429,
    },
    [NcErrorType.ERR_SANDBOX_BLOCKED]: {
        message: (message) => message || 'This operation is not allowed in a sandbox base.',
        code: 403,
    },
    [NcErrorType.ERR_SANDBOX_PRODUCTION_BLOCKED]: {
        message: (message) => message ||
            'This operation is not allowed while a sandbox is active. Make the change in the sandbox instead.',
        code: 403,
    },
};
//# sourceMappingURL=data:application/json;base64,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